import { useRef } from "react";
import styles from "./QuestionsStep.module.css";
import { CoreCard, CoreButton } from "@verity/components/core";
import { Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import i18n from "../../../../i18n";
import { baseTransKey, QuizDraftQuestion, QuizQuestionType } from "./verityAssessmentHelper";
import QuestionEditor from "./QuestionEditor";
import type { QuizQuestionsReturn } from "./hooks/useQuizQuestions";

import MultipleChoiceImage from "../../../../images/quiz-question/question-type-image-multiple-choice.svg?react";
import TrueFalseImage from "../../../../images/quiz-question/question-type-image-true-false.svg?react";
import MatchingImage from "../../../../images/quiz-question/question-type-image-matching.svg?react";
import FillInTheBlankImage from "../../../../images/quiz-question/question-type-image-fill-in-the-blank.svg?react";

import MultipleChoiceIcon from "../../../../images/quiz-question/question-type-icon-multiple-choice.svg?react";
import TrueFalseIcon from "../../../../images/quiz-question/question-type-icon-true-false.svg?react";
import MatchingIcon from "../../../../images/quiz-question/question-type-icon-matching.svg?react";
import FillInTheBlankIcon from "../../../../images/quiz-question/question-type-icon-fill-in-the-blank.svg?react";
import LightbulbIcon from "../../../../images/quiz-question/howto-lightbulb-icon.svg?react";
import VideoPlayIcon from "../../../../images/quiz-question/howto-video-play-icon.svg?react";
import DuplicateIcon from "../../../../images/quiz-question/question-duplicate-icon.svg?react";
import DeleteQuestionIcon from "../../../../images/quiz-question/question-delete-icon.svg?react";

type QuestionTypeConfig = {
	type: QuizQuestionType;
	label: string;
	description: string;
	Image: React.ComponentType<React.SVGProps<SVGSVGElement>>;
	Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const QUESTION_TYPE_CONFIGS: QuestionTypeConfig[] = [
	{
		type: "MultipleChoice",
		get label() {
			return i18n.t(`${baseTransKey}.questions.MultipleChoice.label`) as string;
		},
		get description() {
			return i18n.t(`${baseTransKey}.questions.MultipleChoice.description`) as string;
		},
		Image: MultipleChoiceImage,
		Icon: MultipleChoiceIcon,
	},
	{
		type: "TrueFalse",
		get label() {
			return i18n.t(`${baseTransKey}.questions.TrueFalse.label`) as string;
		},
		get description() {
			return i18n.t(`${baseTransKey}.questions.TrueFalse.description`) as string;
		},
		Image: TrueFalseImage,
		Icon: TrueFalseIcon,
	},
	{
		type: "Matching",
		get label() {
			return i18n.t(`${baseTransKey}.questions.Matching.label`) as string;
		},
		get description() {
			return i18n.t(`${baseTransKey}.questions.Matching.description`) as string;
		},
		Image: MatchingImage,
		Icon: MatchingIcon,
	},
	{
		type: "FillInTheBlank",
		get label() {
			return i18n.t(`${baseTransKey}.questions.FillInTheBlank.label`) as string;
		},
		get description() {
			return i18n.t(`${baseTransKey}.questions.FillInTheBlank.description`) as string;
		},
		Image: FillInTheBlankImage,
		Icon: FillInTheBlankIcon,
	},
];

function questionTypeLabel(type: QuizQuestionType | null): string {
	if (type === null) return i18n.t(`${baseTransKey}.questions.selectType`) as string;
	return i18n.t(`${baseTransKey}.questions.${type}.label`) as string;
}

function SortableQuestionItem({
	q,
	i,
	selectedIndex,
	setSelectedIndex,
	setItemRef,
	onDuplicate,
	onDelete,
}: {
	q: QuizDraftQuestion;
	i: number;
	selectedIndex: number;
	setSelectedIndex: (i: number) => void;
	setItemRef: (el: HTMLDivElement | null) => void;
	onDuplicate: () => void;
	onDelete: () => void;
}) {
	return (
		<Draggable draggableId={String(q.questionOrder)} index={i}>
			{(provided, snapshot) => {
				const setRefs = (el: HTMLDivElement | null) => {
					provided.innerRef(el);
					setItemRef(el);
				};
				return (
					<div
						ref={setRefs}
						{...provided.draggableProps}
						{...provided.dragHandleProps}
						role="button"
						tabIndex={0}
						className={`${styles.questionItem} ${selectedIndex === i ? styles.selected : ""} ${snapshot.isDragging ? styles.dragging : ""}`}
						onClick={() => setSelectedIndex(i)}
						onKeyDown={(e) => {
							if (e.key === "Enter") setSelectedIndex(i);
						}}
					>
						<div className={styles.questionItemContent}>
							<div className={styles.questionItemHeader}>
								<Typography component="span" className={styles.questionItemName}>
									Question {q.questionOrder}
								</Typography>
								<Typography component="span" className={styles.questionItemPoints}>
									{i18n.t(`${baseTransKey}.questions.correctionPoints`, { count: q.correctionPoints })}
								</Typography>
							</div>
							<div className={styles.questionItemBottom}>
								<Typography className={styles.questionItemType}>{questionTypeLabel(q.questionType)}</Typography>
								<div className={styles.questionItemActions}>
									<button
										className={styles.questionItemActionBtn}
										title={i18n.t(`${baseTransKey}.questions.duplicateQuestion`) as string}
										aria-label={i18n.t(`${baseTransKey}.questions.duplicateQuestion`) as string}
										onClick={(e) => {
											e.stopPropagation();
											onDuplicate();
										}}
									>
										<DuplicateIcon className={styles.questionItemActionIcon} />
									</button>
									<button
										className={`${styles.questionItemActionBtn} ${styles.questionItemDeleteBtn}`}
										title={i18n.t(`${baseTransKey}.questions.deleteQuestion`) as string}
										aria-label={i18n.t(`${baseTransKey}.questions.deleteQuestion`) as string}
										onClick={(e) => {
											e.stopPropagation();
											onDelete();
										}}
									>
										<DeleteQuestionIcon className={styles.questionItemActionIcon} />
									</button>
								</div>
							</div>
						</div>
					</div>
				);
			}}
		</Draggable>
	);
}

export default function QuestionsStep({
	questions,
	selectedIndex,
	setSelectedIndex,
	onAddQuestion,
	onDuplicateQuestion,
	onRemoveQuestion,
	onSelectType,
	onSelectedQuestionChange,
	onQuestionsChange,
}: QuizQuestionsReturn) {
	const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

	const handleDragEnd = (result: DropResult) => {
		if (!result.destination) return;
		const oldIndex = result.source.index;
		const newIndex = result.destination.index;
		if (oldIndex === newIndex) return;
		const reordered = Array.from(questions);
		const [moved] = reordered.splice(oldIndex, 1);
		reordered.splice(newIndex, 0, moved);
		onQuestionsChange(reordered.map((q, i) => ({ ...q, questionOrder: i + 1 })));
		if (selectedIndex === oldIndex) {
			setSelectedIndex(newIndex);
		} else if (selectedIndex > oldIndex && selectedIndex <= newIndex) {
			setSelectedIndex(selectedIndex - 1);
		} else if (selectedIndex < oldIndex && selectedIndex >= newIndex) {
			setSelectedIndex(selectedIndex + 1);
		}
		setTimeout(() => {
			itemRefs.current[newIndex]?.focus();
		}, 0);
	};

	const handleQuestionChange = (updatedQuestion: QuizDraftQuestion) => {
		onSelectedQuestionChange(updatedQuestion);
	};

	const selectedQuestion = questions[selectedIndex] ?? null;
	const showTypeSelector = selectedQuestion === null || selectedQuestion.questionType === null;

	return (
		<CoreCard className={styles.questionsCard} slotProps={{ bodyWrapper: { className: styles.questionsBody } }}>
			<CoreCard className={styles.sidebar}>
				<div className={styles.questionList}>
					<DragDropContext onDragEnd={handleDragEnd}>
						<Droppable droppableId="questions">
							{(provided) => (
								<div ref={provided.innerRef} {...provided.droppableProps}>
									{questions.map((q, i) => (
										<SortableQuestionItem
											key={q.questionOrder}
											q={q}
											i={i}
											selectedIndex={selectedIndex}
											setSelectedIndex={setSelectedIndex}
											setItemRef={(el) => {
												itemRefs.current[i] = el;
											}}
											onDuplicate={() => onDuplicateQuestion(i)}
											onDelete={() => onRemoveQuestion(i)}
										/>
									))}
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
				</div>
				<div className={styles.sidebarFooter}>
					<CoreButton
						color="primary"
						startIcon={<AddIcon />}
						onClick={onAddQuestion}
						className={styles.addQuestionButton}
					>
						{i18n.t(`${baseTransKey}.questions.addQuestion`)}
					</CoreButton>
				</div>
			</CoreCard>

			{showTypeSelector ? (
				<div className={styles.content}>
					<div className={styles.typeGrid}>
						{QUESTION_TYPE_CONFIGS.map(({ type, label, description, Image, Icon }) => (
							<button key={type} className={styles.typeCard} onClick={() => onSelectType(type)}>
								<div className={styles.typeCardImage}>
									<Image />
								</div>
								<div className={styles.typeCardInfo}>
									<div className={styles.typeCardIconWrapper}>
										<Icon className={styles.typeCardIcon} />
									</div>
									<div className={styles.typeCardText}>
										<Typography component="span" className={styles.typeCardTitle}>
											{label}
										</Typography>
										<Typography className={styles.typeCardDesc}>{description}</Typography>
									</div>
								</div>
							</button>
						))}
					</div>
					<div className={styles.howToBanner}>
						<LightbulbIcon className={styles.howToBulbIcon} />
						<span className={styles.howTo}>
							<Typography component="span" className={styles.howToLabel}>
								{i18n.t(`${baseTransKey}.questions.howTo.label`)}
							</Typography>
							&nbsp;
							<Typography component="span" className={styles.howToDesc}>
								{i18n.t(`${baseTransKey}.questions.howTo.description`)}
							</Typography>
						</span>
						<button className={styles.watchVideoButton}>
							<VideoPlayIcon className={styles.watchVideoIcon} />
							<Typography component="span" className={styles.watchVideoText}>
								{i18n.t(`${baseTransKey}.questions.howTo.watchVideo`)}
							</Typography>
						</button>
					</div>
				</div>
			) : (
				<CoreCard
					className={styles.questionEditor}
					slotProps={{ bodyWrapper: { className: styles.questionEditorBodyWrapper } }}
				>
					{selectedQuestion?.questionType != null && (
						<QuestionEditor
							question={{ ...selectedQuestion, questionType: selectedQuestion.questionType }}
							onQuestionChange={handleQuestionChange}
						/>
					)}
				</CoreCard>
			)}
		</CoreCard>
	);
}
